import { useCallback, useEffect, useRef } from 'react';
import { peakCrossCorrelation } from '../utils/echoLeak';

interface UseEchoLeakDetectionOptions {
  audioUrl: string | null;
  getMicPcm: (durationMs: number) => Float32Array | null;
  getMicSampleRate: () => number | null;
  onLeakDetected: () => void;
  // Tick rate (ms). Default 500.
  tickMs?: number;
  // Window analysed per tick (ms). Default 500.
  windowMs?: number;
  // peakCorr threshold above which a tick counts as a hit. Default 0.5.
  threshold?: number;
  // Consecutive hits required to fire. Default 2.
  consecutiveHits?: number;
}

interface UseEchoLeakDetectionReturn {
  start: (playbackStartTime: number) => void;
  stop: () => void;
  isReady: boolean;
}

/**
 * Pre-decodes the playback audio for the current sentence and exposes a
 * detector that fires `onLeakDetected` when the mic stream appears to contain
 * the speaker output (i.e. learner is on speakers, not headphones).
 */
export function useEchoLeakDetection(
  opts: UseEchoLeakDetectionOptions,
): UseEchoLeakDetectionReturn {
  const { audioUrl, getMicPcm, getMicSampleRate, onLeakDetected } = opts;
  const tickMs = opts.tickMs ?? 500;
  const windowMs = opts.windowMs ?? 500;
  const threshold = opts.threshold ?? 0.5;
  const consecutiveHits = opts.consecutiveHits ?? 2;

  const decodeCtxRef = useRef<AudioContext | null>(null);
  const playbackPcmRef = useRef<Float32Array | null>(null);
  const playbackSrRef = useRef<number>(0);
  const isReadyRef = useRef(false);

  const timerRef = useRef<number | null>(null);
  const t0Ref = useRef<number>(0);
  const hitCountRef = useRef(0);
  const firedRef = useRef(false);
  const onLeakRef = useRef(onLeakDetected);
  onLeakRef.current = onLeakDetected;

  // Pre-decode the playback audio whenever the URL changes
  useEffect(() => {
    if (!audioUrl) {
      playbackPcmRef.current = null;
      isReadyRef.current = false;
      return;
    }
    let cancelled = false;
    isReadyRef.current = false;
    playbackPcmRef.current = null;

    (async () => {
      try {
        if (!decodeCtxRef.current || decodeCtxRef.current.state === 'closed') {
          const Ctor =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
          decodeCtxRef.current = new Ctor();
        }
        const ctx = decodeCtxRef.current;
        const resp = await fetch(audioUrl);
        if (!resp.ok) throw new Error(`fetch ${audioUrl} -> ${resp.status}`);
        const buf = await resp.arrayBuffer();
        const decoded = await ctx.decodeAudioData(buf.slice(0));
        if (cancelled) return;
        // Use channel 0 (mono mix is fine; original is mono in our pipeline)
        playbackPcmRef.current = decoded.getChannelData(0).slice();
        playbackSrRef.current = decoded.sampleRate;
        isReadyRef.current = true;
      } catch (err) {
        console.warn('[echo-leak] decode failed; detection disabled:', err);
        playbackPcmRef.current = null;
        isReadyRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [audioUrl]);

  const stop = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    hitCountRef.current = 0;
    firedRef.current = false;
  }, []);

  const start = useCallback(
    (playbackStartTime: number) => {
      stop();
      t0Ref.current = playbackStartTime;
      hitCountRef.current = 0;
      firedRef.current = false;

      timerRef.current = window.setInterval(() => {
        if (firedRef.current) return;
        if (!isReadyRef.current) return;
        const play = playbackPcmRef.current;
        if (!play) return;

        const micSr = getMicSampleRate();
        if (!micSr) return;

        const now = performance.now();
        const elapsedSec = (now - t0Ref.current) / 1000;
        // Need both signals to overlap by at least `windowMs` worth of playback,
        // plus headroom for the max-lag scan.
        if (elapsedSec < (windowMs + 350) / 1000) return;

        const mic = getMicPcm(windowMs);
        if (!mic) return;

        const playSr = playbackSrRef.current;
        const playWindowSamples = Math.floor((windowMs / 1000) * playSr);
        const playEnd = Math.min(play.length, Math.floor(elapsedSec * playSr));
        const playStart = Math.max(0, playEnd - playWindowSamples);
        if (playEnd - playStart < playWindowSamples * 0.8) return;
        const playWindow = play.subarray(playStart, playEnd);

        // Cross-correlation expects matching sample rates. Mic and play
        // contexts usually share the system rate, but if not, downsample to a
        // common low rate for the correlation.
        const corrSr = Math.min(micSr, playSr);
        // We pass arrays as-is; peakCrossCorrelation downsamples internally
        // assuming the same source rate. If rates differ, decimate the higher
        // one to match.
        const micAligned =
          micSr === corrSr
            ? mic
            : decimateTo(mic, micSr, corrSr);
        const playAligned =
          playSr === corrSr
            ? playWindow
            : decimateTo(playWindow, playSr, corrSr);

        const peak = peakCrossCorrelation(micAligned, playAligned, {
          sourceSampleRate: corrSr,
        });

        if (peak >= threshold) {
          hitCountRef.current += 1;
          if (hitCountRef.current >= consecutiveHits) {
            firedRef.current = true;
            stop();
            try {
              onLeakRef.current();
            } catch (err) {
              console.error('[echo-leak] callback failed', err);
            }
          }
        } else {
          hitCountRef.current = 0;
        }
      }, tickMs);
    },
    [getMicPcm, getMicSampleRate, stop, threshold, consecutiveHits, tickMs, windowMs],
  );

  useEffect(() => {
    return () => {
      stop();
      if (decodeCtxRef.current && decodeCtxRef.current.state !== 'closed') {
        decodeCtxRef.current.close().catch(() => {
          /* ignore */
        });
        decodeCtxRef.current = null;
      }
    };
  }, [stop]);

  return { start, stop, isReady: isReadyRef.current };
}

function decimateTo(input: Float32Array, srcSr: number, dstSr: number): Float32Array {
  const ratio = Math.max(1, Math.round(srcSr / dstSr));
  if (ratio === 1) return input;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) out[i] = input[i * ratio];
  return out;
}
